'use client'

import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useId, useRef, useState } from 'react'

import Image from '@/ui/MediaImage'
import { toMediaCdnUrl } from '@/utils/imageUrl'

import { formatVideoClock } from '../../../../utils/formatVideoClock'
import S from './NewsArticleVideoPlayer.module.scss'
import {
  NewsPlayerIconFullscreen,
  NewsPlayerIconLoading,
  NewsPlayerIconPause,
  NewsPlayerIconPip,
  NewsPlayerIconPlay,
  NewsPlayerIconPlayLarge,
  NewsPlayerIconSettings,
  NewsPlayerIconVolumeOff,
  NewsPlayerIconVolumeOn
} from './NewsArticleVideoPlayerIcons'

type NewsArticleVideoPlayerProps = {
  videoUrl: string
  posterUrl?: string
  title: string
}

const CHROME_IDLE_MS = 3000
const LOADING_SHOW_DELAY_MS = 1000
const PORTRAIT_MAX_HEIGHT = '80vh'

const NewsArticleVideoPlayer = ({
  videoUrl,
  posterUrl,
  title
}: NewsArticleVideoPlayerProps) => {
  const t = useTranslations('news.detail.video')
  const volumePopoverId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const volumeGroupRef = useRef<HTMLDivElement>(null)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Volume to come back to when unmuting: muting drives the slider to 0, which
  // would otherwise lose whatever level the viewer had set.
  const volumeBeforeMuteRef = useRef(1)
  const [hasStarted, setHasStarted] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [volume, setVolume] = useState(1)
  const [isPointerInside, setIsPointerInside] = useState(false)
  const [isChromeIdle, setIsChromeIdle] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isBuffering, setIsBuffering] = useState(false)
  const [loadingDelayArmed, setLoadingDelayArmed] = useState(false)
  const [aspect, setAspect] = useState<number | null>(null)

  const pipSupported =
    typeof document !== 'undefined' &&
    'pictureInPictureEnabled' in document &&
    Boolean(document.pictureInPictureEnabled)

  const isMuted = volume < 0.001
  const showPoster = Boolean(
    posterUrl && !hasStarted && !isPlaying && currentTime <= 0.05
  )
  const isPendingLoad = hasStarted && (isLoading || isBuffering) && !showPoster
  const showLoadingOverlay = isPendingLoad && loadingDelayArmed
  const chromeIsIdle = isPlaying && isPointerInside && isChromeIdle
  const showChrome =
    hasStarted &&
    !showLoadingOverlay &&
    (!isPlaying || (isPointerInside && !chromeIsIdle))
  const effectiveShowChrome = showChrome
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0
  const aspectStyle = aspect
    ? ({
        '--news-video-aspect': `${aspect}`,
        ...(aspect < 1
          ? {
              '--news-video-max-width': `calc(${aspect} * ${PORTRAIT_MAX_HEIGHT})`
            }
          : {})
      } as React.CSSProperties)
    : undefined

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }
  }, [])

  const scheduleIdleTimer = useCallback(() => {
    clearIdleTimer()
    idleTimerRef.current = setTimeout(() => {
      setIsChromeIdle(true)
    }, CHROME_IDLE_MS)
  }, [clearIdleTimer])

  const wakeChrome = useCallback(() => {
    setIsChromeIdle(false)
    if (isPlaying && isPointerInside) {
      scheduleIdleTimer()
    }
  }, [isPlaying, isPointerInside, scheduleIdleTimer])

  useEffect(() => {
    if (!isPendingLoad) {
      return
    }

    const timer = setTimeout(() => {
      setLoadingDelayArmed(true)
    }, LOADING_SHOW_DELAY_MS)

    return () => {
      clearTimeout(timer)
      setLoadingDelayArmed(false)
    }
  }, [isPendingLoad])

  useEffect(() => {
    const video = videoRef.current
    if (!video) {
      return
    }
    video.volume = volume
    video.muted = volume < 0.001
  }, [volume])

  useEffect(() => {
    if (!isPlaying || !isPointerInside) {
      clearIdleTimer()
      return
    }

    scheduleIdleTimer()
    return clearIdleTimer
  }, [clearIdleTimer, isPlaying, isPointerInside, scheduleIdleTimer])

  useEffect(() => clearIdleTimer, [clearIdleTimer])

  const handlePlay = async () => {
    const video = videoRef.current
    if (!video) {
      return
    }

    setHasStarted(true)
    setIsLoading(true)

    try {
      await video.play()
    } catch {
      setIsLoading(false)
    }
  }

  const handlePause = () => {
    videoRef.current?.pause()
  }

  const togglePlay = () => {
    if (isPlaying) {
      handlePause()
      return
    }
    void handlePlay()
  }

  const handleVolumeChange = (value: number) => {
    if (value >= 0.001) {
      volumeBeforeMuteRef.current = value
    }
    setVolume(value)
    wakeChrome()
  }

  const toggleMute = () => {
    setVolume(current =>
      current < 0.001 ? volumeBeforeMuteRef.current || 1 : 0
    )
    wakeChrome()
  }

  const handleVideoClick = () => {
    if (!hasStarted || showPoster) {
      return
    }
    togglePlay()
    wakeChrome()
  }

  const handleSeek = (value: number) => {
    const video = videoRef.current
    if (!video || !Number.isFinite(value)) {
      return
    }
    video.currentTime = value
    setCurrentTime(value)
    wakeChrome()
  }

  const toggleFullscreen = async () => {
    const container = containerRef.current
    if (!container) {
      return
    }
    if (document.fullscreenElement) {
      await document.exitFullscreen()
      return
    }
    await container.requestFullscreen()
    wakeChrome()
  }

  const togglePip = async () => {
    const video = videoRef.current
    if (!video || !pipSupported) {
      return
    }
    if (document.pictureInPictureElement) {
      await document.exitPictureInPicture()
      return
    }
    await video.requestPictureInPicture()
    wakeChrome()
  }

  const handlePointerEnter = () => {
    setIsPointerInside(true)
    setIsChromeIdle(false)
  }

  const handlePointerLeave = () => {
    setIsPointerInside(false)
    setIsChromeIdle(false)
    clearIdleTimer()
  }

  const handlePointerMove = () => {
    wakeChrome()
  }

  const handleTouchStart = () => {
    setIsPointerInside(true)
    wakeChrome()
  }

  return (
    <div
      ref={containerRef}
      className={S.root}
      style={aspectStyle}
      onMouseEnter={handlePointerEnter}
      onMouseLeave={handlePointerLeave}
      onMouseMove={handlePointerMove}
      onTouchStart={handleTouchStart}
    >
      <video
        ref={videoRef}
        src={toMediaCdnUrl(videoUrl)}
        className={S.video}
        playsInline
        preload="metadata"
        aria-label={title}
        onClick={handleVideoClick}
        onLoadStart={() => {
          if (hasStarted) {
            setIsLoading(true)
          }
        }}
        onLoadedMetadata={event => {
          const video = event.currentTarget
          setDuration(video.duration || 0)
          if (video.videoWidth > 0 && video.videoHeight > 0) {
            setAspect(video.videoWidth / video.videoHeight)
          }
        }}
        onCanPlay={() => {
          setIsLoading(false)
        }}
        onTimeUpdate={event => {
          setCurrentTime(event.currentTarget.currentTime)
        }}
        onWaiting={() => {
          if (hasStarted) {
            setIsBuffering(true)
          }
        }}
        onPlaying={() => {
          setIsPlaying(true)
          setIsLoading(false)
          setIsBuffering(false)
        }}
        onPlay={() => {
          setIsPlaying(true)
        }}
        onPause={event => {
          setIsPlaying(false)
          setIsBuffering(false)
          if (event.currentTarget.currentTime <= 0.05) {
            setCurrentTime(0)
          }
        }}
        onEnded={() => {
          setIsPlaying(false)
          setIsBuffering(false)
        }}
        onError={() => {
          setIsLoading(false)
          setIsBuffering(false)
        }}
      />

      {showPoster && posterUrl ? (
        <div className={S.posterLayer}>
          <Image
            src={posterUrl}
            alt=""
            fill
            sizes="(max-width: 768px) 100vw, 1000px"
            className={S.poster}
          />
          <button
            type="button"
            className={S.centerPlay}
            aria-label={t('playAria')}
            onClick={() => {
              void handlePlay()
            }}
          >
            <span className={S.centerPlayIcon} aria-hidden>
              <NewsPlayerIconPlayLarge className={S.centerPlaySvg} />
            </span>
          </button>
        </div>
      ) : null}

      {showLoadingOverlay ? (
        <>
          <div className={S.loadingBackdrop} aria-hidden />
          <div
            className={S.loadingOverlay}
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label={t('loadingAria')}
          >
            <NewsPlayerIconLoading className={S.loadingSpinner} />
          </div>
        </>
      ) : null}

      <div className={clsx(S.chrome, !effectiveShowChrome && S.chromeHidden)}>
        <div className={S.progressTrack}>
          <div className={S.progressFill} style={{ width: `${progress}%` }} />
          <input
            type="range"
            className={S.progressInput}
            min={0}
            max={duration || 0}
            step={0.1}
            value={Math.min(currentTime, duration || 0)}
            aria-label={t('seekAria')}
            onChange={event => handleSeek(Number(event.target.value))}
          />
        </div>

        <div className={S.controls}>
          <div className={S.controlsLeft}>
            <button
              type="button"
              className={S.iconButton}
              aria-label={isPlaying ? t('pauseAria') : t('playAria')}
              onClick={togglePlay}
            >
              {isPlaying ? (
                <NewsPlayerIconPause className={S.icon} />
              ) : (
                <NewsPlayerIconPlay className={S.icon} />
              )}
            </button>
            <div ref={volumeGroupRef} className={S.volumeGroup}>
              <button
                type="button"
                className={S.iconButton}
                aria-label={isMuted ? t('unmuteAria') : t('muteAria')}
                aria-pressed={isMuted}
                aria-controls={volumePopoverId}
                onClick={toggleMute}
              >
                {isMuted ? (
                  <NewsPlayerIconVolumeOff className={S.icon} />
                ) : (
                  <NewsPlayerIconVolumeOn className={S.icon} />
                )}
              </button>
              <div
                id={volumePopoverId}
                className={S.volumeSliderWrap}
                role="group"
              >
                <input
                  type="range"
                  className={S.volumeSlider}
                  min={0}
                  max={1}
                  step={0.05}
                  value={volume}
                  aria-label={t('volumeAria')}
                  style={
                    {
                      '--volume-percent': `${volume * 100}%`
                    } as React.CSSProperties
                  }
                  onChange={event =>
                    handleVolumeChange(Number(event.target.value))
                  }
                />
              </div>
            </div>
            <span className={S.time}>
              {formatVideoClock(currentTime)} / {formatVideoClock(duration)}
            </span>
          </div>

          <div className={S.controlsRight}>
            <button
              type="button"
              className={`${S.iconButton} ${S.iconButtonDim}`}
              aria-label={t('settingsAria')}
              disabled
              tabIndex={-1}
            >
              <NewsPlayerIconSettings className={S.icon} />
            </button>
            <button
              type="button"
              className={S.iconButton}
              aria-label={t('pipAria')}
              disabled={!pipSupported}
              onClick={() => {
                void togglePip()
              }}
            >
              <NewsPlayerIconPip className={S.icon} />
            </button>
            <button
              type="button"
              className={S.iconButton}
              aria-label={t('fullscreenAria')}
              onClick={() => {
                void toggleFullscreen()
              }}
            >
              <NewsPlayerIconFullscreen className={S.icon} />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NewsArticleVideoPlayer
